import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { useState, useEffect } from 'react'
import { WordLength } from '../types/game'
import { GAME_MODES } from '../constants/gameConfig'
import { trackAnagramModeSelected } from '../services/gameAnalytics'
import { getAnagramPlayedModes, getAnagramCompletedDetail, AnagramCompletedDetail } from '../utils/storage'
import { ChallengeCard } from '../components/ChallengeCard'

// Only 4/5/6 letters - matches isValidMode() in utils/validation.ts, which
// doesn't accept '3' yet even though GAME_MODES has a 3-letter entry (a
// pre-existing gap in Zword's own mode-select too, not something introduced
// here - not fixed here since it's outside Anagram's scope and needs
// confirming 3-letter target words are actually ready).
const MODE_ORDER: WordLength[] = [6, 5, 4]

/**
 * Anagram's mode-select screen, reusing GameModePage.tsx's ChallengeCard
 * component and GAME_MODES so the two games look identical (same card
 * shape, same "Play"/"Already Played" button) - Anagram scrambles the same
 * daily words Zword guesses.
 */
export const AnagramModePage = () => {
  const navigate = useNavigate()
  const [playedModes, setPlayedModes] = useState<WordLength[]>([])
  const [completedDetail, setCompletedDetail] = useState<Record<number, AnagramCompletedDetail>>({})

  useEffect(() => {
    setPlayedModes(getAnagramPlayedModes())
    setCompletedDetail(getAnagramCompletedDetail() || {})
  }, [])

  const isModePlayed = (wordLength: WordLength): boolean => playedModes.includes(wordLength)

  const handleModeSelect = (wordLength: WordLength) => {
    if (isModePlayed(wordLength)) return

    try {
      trackAnagramModeSelected(wordLength)
    } catch (error) {
      console.error('Failed to track anagram mode selection:', error)
    }

    navigate(`/anagram/play/${wordLength}`)
  }

  const container = {
    hidden: { opacity: 0 },
    show: { opacity: 1, transition: { staggerChildren: 0.15 } },
  }

  const item = {
    hidden: { y: 20, opacity: 0 },
    show: { y: 0, opacity: 1 },
  }

  return (
    <div className="mode-selection">
      <div className="flex justify-center items-center mb-8">
        <h1 className="mode-title">Anagram</h1>
      </div>
      <p className="mode-subtitle">Unscramble the letters to find today's word</p>

      <motion.div className="mode-cards" variants={container} initial="hidden" animate="show">
        {MODE_ORDER.map((wordLength) => {
          const mode = GAME_MODES[wordLength]
          const played = isModePlayed(wordLength)
          return (
            <motion.div key={wordLength} variants={item}>
              <ChallengeCard
                wordLength={wordLength}
                description={mode.description || ''}
                examples={mode.examples || []}
                isLocked={false}
                hasPlayedToday={played}
                revealedWord={completedDetail[wordLength]?.word}
                revealedWon={completedDetail[wordLength]?.won}
                daysCompleted={0}
                totalDays={0}
                onPlay={() => handleModeSelect(wordLength)}
              />
            </motion.div>
          )
        })}
      </motion.div>
    </div>
  )
}
